import { useViewport } from 'contexts/viewport';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'shell/components/Box';
import IconButton from 'shell/components/IconButton';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import Tooltip from 'shell/components/Tooltip';
import { formatVideoTime, VIDEO_EVIDENCE_STATUSES } from 'utils/videoGrading';
import { evidenceColor, EvidenceStatusTag } from './EvidenceTags';

const VideoEvidenceMap = ({ evidence, criteria, currentTime, duration, selectedCriterionId, onSeek }) => {
    const { t } = useTranslation('assignment');
    const { device } = useViewport();
    const [zoom, setZoom] = useState(1);
    const rows = useMemo(
        () =>
            criteria.map((criterion) => ({
                ...criterion,
                evidence: evidence.filter(
                    (item) => item.criterionId === criterion.id && item.start !== null && item.end <= duration
                ),
            })),
        [criteria, duration, evidence]
    );
    const tickCount = (device === 'mobile' ? 3 : 6) * zoom;
    const ticks = Array.from({ length: tickCount + 1 }, (_, index) => (duration * index) / tickCount);
    const hasTimedEvidence = rows.some((row) => row.evidence.length);

    return (
        <Stack
            spacing={3}
            sx={(theme) => ({
                p: 4,
                borderBottom: `1px solid ${theme.palette.ensightful.neutral[300]}`,
                background: theme.palette.ensightful.shades[0],
            })}
        >
            <Stack direction={'row'} alignItems={'center'} useFlexGap flexWrap={'wrap'} spacing={2}>
                <Text component={'h2'} variant={'p2'} color={'light'}>
                    {t('video-grading.evidence-map', 'Evidence Map')}
                </Text>
                <Stack
                    direction={'row'}
                    useFlexGap
                    flexWrap={'wrap'}
                    sx={(theme) => ({ borderRadius: '40px', background: theme.palette.ensightful.neutral[100] })}
                >
                    {VIDEO_EVIDENCE_STATUSES.map((status) => (
                        <EvidenceStatusTag key={status} status={status} legend />
                    ))}
                </Stack>
                <Stack direction={'row'} alignItems={'center'} spacing={1} sx={{ ml: 'auto' }}>
                    <IconButton
                        kind={'subtract'}
                        size={'small'}
                        variant={'square'}
                        disabled={zoom === 1}
                        aria-label={t('video-grading.zoom-out', 'Zoom out')}
                        onClick={() => setZoom((value) => Math.max(1, value / 2))}
                    />
                    <Text variant={'p3'}>{t('video-grading.zoom', '{{zoom}}× in view', { zoom })}</Text>
                    <IconButton
                        kind={'add'}
                        size={'small'}
                        variant={'square'}
                        disabled={zoom === 8}
                        aria-label={t('video-grading.zoom-in', 'Zoom in')}
                        onClick={() => setZoom((value) => Math.min(8, value * 2))}
                    />
                </Stack>
            </Stack>
            {duration > 0 && hasTimedEvidence ? (
                <Stack direction={'row'} spacing={2}>
                    <Stack sx={{ width: 100, flexShrink: 0, pt: 5 }}>
                        {rows.map((row) => (
                            <Text
                                key={row.id}
                                variant={'p3'}
                                noWrap
                                title={row.title}
                                sx={{ height: 28, lineHeight: '28px' }}
                                color={row.id === selectedCriterionId ? 'primary' : 'default'}
                            >
                                {row.title}
                            </Text>
                        ))}
                    </Stack>
                    <Box
                        sx={{ overflowX: 'auto', minWidth: 0, flex: 1 }}
                        tabIndex={0}
                        aria-label={t('video-grading.timeline', 'Video evidence timeline')}
                    >
                        <Box sx={{ width: `${zoom * 100}%`, minWidth: '100%', pt: 5, position: 'relative', pb: 2 }}>
                            <Box
                                sx={(theme) => ({
                                    position: 'absolute',
                                    left: `${Math.min(100, (currentTime / duration) * 100)}%`,
                                    top: 0,
                                    bottom: 6,
                                    borderLeft: `1px solid ${theme.palette.ensightful.neutral[700]}`,
                                    zIndex: 1,
                                    pointerEvents: 'none',
                                })}
                            >
                                <Text
                                    variant={'p3'}
                                    sx={(theme) => ({
                                        position: 'absolute',
                                        top: 0,
                                        px: 1,
                                        borderRadius: '4px',
                                        whiteSpace: 'nowrap',
                                        width: 'max-content',
                                        maxWidth: 'none',
                                        transform: currentTime / duration > 0.85 ? 'translateX(-100%)' : undefined,
                                        background: theme.palette.ensightful.neutral[700],
                                        color: theme.palette.ensightful.shades[0],
                                    })}
                                >
                                    {formatVideoTime(currentTime)}
                                </Text>
                            </Box>
                            {rows.map((row) => (
                                <Box key={row.id} sx={{ height: 28, display: 'flex', alignItems: 'center' }}>
                                    <Box
                                        sx={(theme) => ({
                                            height: 14,
                                            width: '100%',
                                            borderRadius: '40px',
                                            background: theme.palette.ensightful.neutral[100],
                                            position: 'relative',
                                        })}
                                    >
                                        {row.evidence.map((item) => {
                                            const label = t(
                                                'video-grading.timeline-evidence',
                                                '{{criterion}}: {{status}}, {{start}}–{{end}}',
                                                {
                                                    criterion: row.title,
                                                    status: t(`video-grading.${item.status}`, item.status),
                                                    start: formatVideoTime(item.start),
                                                    end: formatVideoTime(item.end),
                                                }
                                            );
                                            return (
                                                <Tooltip key={item.id} title={label}>
                                                    <Box
                                                        component={'button'}
                                                        type={'button'}
                                                        aria-label={label}
                                                        onClick={() => onSeek(item)}
                                                        sx={(theme) => ({
                                                            position: 'absolute',
                                                            left: `${(item.start / duration) * 100}%`,
                                                            width: `${((item.end - item.start) / duration) * 100}%`,
                                                            minWidth: 4,
                                                            height: 14,
                                                            padding: 0,
                                                            border: 0,
                                                            borderRadius: '40px',
                                                            cursor: 'pointer',
                                                            background: evidenceColor(theme, item.status),
                                                            '&:focus-visible': {
                                                                outline: `2px solid ${theme.palette.ensightful.primary[500]}`,
                                                                outlineOffset: '3px',
                                                            },
                                                            '&:hover': {
                                                                boxShadow: `0 0 0 2px ${theme.palette.ensightful.primary[200]}`,
                                                            },
                                                        })}
                                                    />
                                                </Tooltip>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            ))}
                            <Stack direction={'row'} justifyContent={'space-between'} sx={{ mt: 1 }}>
                                {ticks.map((tick, index) => (
                                    <Text key={index} variant={'p3'} color={'light'}>
                                        {formatVideoTime(tick)}
                                    </Text>
                                ))}
                            </Stack>
                        </Box>
                    </Box>
                </Stack>
            ) : (
                <Text variant={'p3'} color={'light'}>
                    {t('video-grading.no-timed-evidence', 'No timestamped evidence is available for this video.')}
                </Text>
            )}
        </Stack>
    );
};

export default VideoEvidenceMap;
